/**
 * The Messages page's timeline scrubber: turning the message index into a histogram you can drag a
 * range out of.
 *
 * All of the arithmetic lives here rather than in the component, because the interesting cases are
 * arithmetic ones — an empty zone, a zone whose messages all landed in the same second, a drag that
 * ends outside the track or runs right to left — and each of those is a division by zero or an
 * inverted range waiting to reach the API as a 400.
 *
 * The window it produces is half-open, `[since, until)`, matching `time-window.ts` and the backend's
 * `TimeWindow`.
 */

/** Just enough of a `ZoneMessageIndexEntry` to place it on a timeline. */
export interface Placeable {
  sent_at: string
}

export interface Bucket {
  /** Inclusive lower edge. */
  start: number
  /** Exclusive upper edge — except the last bucket, which owns the final instant (see `bucket`). */
  end: number
  count: number
}

export interface Span {
  from: number
  to: number
}

/**
 * The extent of the index, in epoch millis, or `undefined` when there is nothing to draw.
 *
 * A zone with ONE message (or several sharing an instant) has a zero-width span, which every
 * downstream pixel calculation would divide by. Rather than special-casing that at each call site,
 * it is widened here to a minute so the single message renders as one bar in the middle of a track
 * instead of vanishing or producing `NaN`.
 */
export function extent(entries: readonly Placeable[]): Span | undefined {
  let from = Number.POSITIVE_INFINITY
  let to = Number.NEGATIVE_INFINITY
  for (const entry of entries) {
    const at = Date.parse(entry.sent_at)
    if (Number.isNaN(at)) continue
    if (at < from) from = at
    if (at > to) to = at
  }
  if (from === Number.POSITIVE_INFINITY) return undefined
  if (from === to) {
    const pad = 30_000
    return { from: from - pad, to: to + pad }
  }
  return { from, to }
}

/**
 * Message counts across `count` equal buckets of the span.
 *
 * The LAST bucket is closed at both ends. Every other is half-open, so an entry sitting exactly on
 * an internal edge belongs to the later bucket and is counted once; without the exception the newest
 * message in the zone — which is always exactly at `to` — would fall off the end of the chart it is
 * supposed to define.
 */
export function buckets(
  entries: readonly Placeable[],
  span: Span,
  count: number,
): Bucket[] {
  const n = Math.max(1, Math.floor(count))
  const width = (span.to - span.from) / n
  const out: Bucket[] = Array.from({ length: n }, (_, i) => ({
    start: span.from + i * width,
    end: span.from + (i + 1) * width,
    count: 0,
  }))
  for (const entry of entries) {
    const at = Date.parse(entry.sent_at)
    if (Number.isNaN(at) || at < span.from || at > span.to) continue
    const index = Math.min(n - 1, Math.floor((at - span.from) / width))
    const target = out[index]
    if (target !== undefined) target.count += 1
  }
  return out
}

/** Where an instant sits on a track of `width` pixels. Clamped, so a stray value cannot escape it. */
export function toPixel(at: number, span: Span, width: number): number {
  const ratio = (at - span.from) / (span.to - span.from)
  return Math.max(0, Math.min(width, ratio * width))
}

/** The inverse. Also clamped: a pointer can leave the element mid-drag, and often does. */
export function toInstant(x: number, span: Span, width: number): number {
  if (width <= 0) return span.from
  const ratio = Math.max(0, Math.min(1, x / width))
  return Math.round(span.from + ratio * (span.to - span.from))
}

/**
 * The window a drag from `x1` to `x2` selects, as ISO instants ready for the URL.
 *
 * Returns `undefined` for a drag too short to be meaningful — a click, in other words, which must
 * not silently narrow the view to a zero-width window the API would reject. Right-to-left drags are
 * normalised rather than refused: dragging backwards is a perfectly ordinary way to select, and
 * refusing it would look like the control was broken.
 */
export function selectionFromDrag(
  x1: number,
  x2: number,
  span: Span,
  width: number,
  minimumPx = 4,
): { since: string; until: string } | undefined {
  if (Math.abs(x2 - x1) < minimumPx) return undefined
  const lo = Math.min(x1, x2)
  const hi = Math.max(x1, x2)
  // Symmetrically with the right edge below: a drag that begins within a pixel or two of the left
  // edge means "from the beginning". Without this the first pixel of the track is a dead zone worth
  // real time -- on a 400-day zone in an 800px strip, one pixel is over an hour.
  const a = lo <= EDGE_PX ? span.from : toInstant(lo, span, width)
  // A drag that reaches the right-hand edge means "up to and including the newest message", but the
  // window is half-open and the newest message sits EXACTLY on `span.to` -- so an exact upper bound
  // would exclude the very message defining that edge. Dragging the full track would then quietly
  // fail to select everything, which is the one thing a full-width drag obviously means. Same
  // reasoning as `selectionFromBucket`'s last bucket.
  const atRightEdge = hi >= width - EDGE_PX
  const b = atRightEdge ? span.to + 1000 : toInstant(hi, span, width)
  if (a >= b) return undefined
  return { since: new Date(a).toISOString(), until: new Date(b).toISOString() }
}

/** How close to the end of the track still counts as "the end". A drag rarely lands on the pixel. */
const EDGE_PX = 2

/**
 * The window a single bucket selects, for click-a-bar.
 *
 * The last bucket's upper edge is nudged past the final instant, because the window is half-open:
 * without it, clicking the newest bar excludes the newest message, which is the one thing a reader
 * clicking the rightmost bar is unambiguously asking to see.
 */
export function selectionFromBucket(
  bucket: Bucket,
  span: Span,
): { since: string; until: string } {
  const end = bucket.end >= span.to ? span.to + 1000 : bucket.end
  return {
    since: new Date(Math.round(bucket.start)).toISOString(),
    until: new Date(Math.round(end)).toISOString(),
  }
}

/**
 * The currently selected window as pixel offsets, for drawing the highlight.
 *
 * An open-ended selection is drawn to the edge of the track rather than skipped: `?since=` alone is
 * a legal window and a reader who set one must be able to see what it selected.
 */
export function selectionPixels(
  bounds: { since?: string; until?: string },
  span: Span,
  width: number,
): { left: number; right: number } | undefined {
  const since = bounds.since === undefined ? undefined : Date.parse(bounds.since)
  const until = bounds.until === undefined ? undefined : Date.parse(bounds.until)
  const hasSince = since !== undefined && !Number.isNaN(since)
  const hasUntil = until !== undefined && !Number.isNaN(until)
  if (!hasSince && !hasUntil) return undefined
  const left = hasSince ? toPixel(since as number, span, width) : 0
  const right = hasUntil ? toPixel(until as number, span, width) : width
  return left <= right ? { left, right } : { left: right, right: left }
}
